import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { QuoteApprovalApiService } from '../../services/quote-approval-api.service';
import {
  QUOTE_APPROVAL_DECISION_ACTION_LABELS,
  QuoteApprovalDecideRequest,
  QuoteApprovalDecision
} from '../../models/quote-approval.model';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { ToastService } from '../../../../shared/services/toast.service';

/**
 * Modal de confirmación de decisión del aprobador (RF-002): Aprobar / Solicitar cambios / Rechazar
 * con comentario opcional. Hace el POST decide desde el CRM y notifica con toast; el padre
 * refresca al recibir `decided` (también se emite tras un 409 para mostrar el estado real).
 */
@Component({
  selector: 'app-quote-approval-decision-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './quote-approval-decision-modal.component.html'
})
export class QuoteApprovalDecisionModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() approvalId: number | null = null;
  @Input() decision: QuoteApprovalDecision | null = null;
  @Input() quoteNumber = '';

  @Output() closed = new EventEmitter<void>();
  @Output() decided = new EventEmitter<void>();

  comment = '';
  submitting = false;
  error = '';

  readonly COMMENT_MAX = 2000;

  constructor(
    private approvalApi: QuoteApprovalApiService,
    private authService: AuthService,
    private toast: ToastService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.comment = '';
      this.error = '';
      this.submitting = false;
    }
  }

  get actionLabel(): string {
    return this.decision ? QUOTE_APPROVAL_DECISION_ACTION_LABELS[this.decision] : '';
  }

  get title(): string {
    switch (this.decision) {
      case 'APPROVED': return 'Aprobar cotización';
      case 'REJECTED': return 'Rechazar cotización';
      case 'CHANGES_REQUESTED': return 'Solicitar cambios';
      default: return '';
    }
  }

  get confirmMessage(): string {
    switch (this.decision) {
      case 'APPROVED':
        return 'La cotización pasará a "Aprobada" y podrá publicarse o enviarse al cliente.';
      case 'REJECTED':
        return 'La cotización volverá a "Borrador" y se notificará al solicitante.';
      case 'CHANGES_REQUESTED':
        return 'La cotización volverá a "Borrador" para que el solicitante aplique los cambios.';
      default:
        return '';
    }
  }

  /** Clases del botón de confirmación según la decisión (verde / rojo / ámbar). */
  get confirmButtonClass(): string {
    switch (this.decision) {
      case 'APPROVED':
        return 'bg-success-600 hover:bg-success-700';
      case 'REJECTED':
        return 'bg-error-600 hover:bg-error-700';
      case 'CHANGES_REQUESTED':
        return 'bg-amber-500 hover:bg-amber-600';
      default:
        return 'bg-brand-600 hover:bg-brand-700';
    }
  }

  get commentLength(): number {
    return this.comment.length;
  }

  confirmar(): void {
    if (this.submitting || !this.approvalId || !this.decision) return;
    this.error = '';

    if (this.comment.length > this.COMMENT_MAX) {
      this.error = `El comentario no puede superar los ${this.COMMENT_MAX} caracteres`;
      return;
    }

    const request: QuoteApprovalDecideRequest = { decision: this.decision };
    const comment = this.comment.trim();
    if (comment) request.comment = comment;

    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.submitting = true;
    this.approvalApi.decide(this.approvalId, businessId, request).subscribe({
      next: (approval) => {
        this.submitting = false;
        const sufijo = this.quoteNumber || approval.quoteNumber;
        switch (approval.status) {
          case 'APPROVED':
            this.toast.success({ title: 'Cotización aprobada', message: `Aprobaste la cotización ${sufijo}` });
            break;
          case 'REJECTED':
            this.toast.success({ title: 'Cotización rechazada', message: `Rechazaste la cotización ${sufijo}; volvió a Borrador` });
            break;
          default:
            this.toast.success({ title: 'Cambios solicitados', message: `Solicitaste cambios en la cotización ${sufijo}; volvió a Borrador` });
        }
        this.decided.emit();
      },
      error: (err) => {
        this.submitting = false;
        const msg = err.error?.message || 'No se pudo registrar la decisión';
        if (err?.status === 409) {
          // Ya fue decidida (por correo o por otro flujo): avisar y dejar que el padre refresque.
          this.toast.error({ title: 'Solicitud ya decidida', message: msg });
          this.decided.emit();
        } else {
          this.error = msg;
          this.toast.error({ title: 'Error', message: msg });
        }
      }
    });
  }

  cancelar(): void {
    if (this.submitting) return;
    this.closed.emit();
  }
}
